<template>
  <PageLayout class="referral-leaderboard-layout">
    <TableList
      class="referral-leaderboard-page"
      row-key="account"
      noAction
      noHeadBtn
      :loading="loading"
      :title="$t(`${i18nPrefix}.title`)"
      :listData="leaderboardList"
      :tableColumns="tableColumns"
      :pagination="pagination"
      :defaultSort="defaultSort"
      @pagination-change="handlePaginationChange"
      @sort-change="handleSortChange"
    >
      <template #filter>
        <div class="filter-control company-filter">
          <span class="filter-label">{{ $t(`${i18nPrefix}.filter.company`) }}</span>
          <el-select
            v-model="filters.companyId"
            class="company-select"
            filterable
            clearable
            @change="handleFilterChange"
          >
            <el-option
              v-for="company in companyList"
              :key="company.id"
              :label="company.i18nName"
              :style="{ paddingLeft: (company.level || 1) * 15 + 'px' }"
              :value="company.id"
            />
          </el-select>
        </div>
        <el-select
          v-model="filters.roleType"
          class="filter-control referrer-type-filter"
          :placeholder="$t(`${i18nPrefix}.filter.referrerType`)"
          @change="handleFilterChange"
        >
          <el-option
            v-for="item in referrerTypeList"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <el-input
          v-model="filters.keyword"
          class="filter-control keyword-input"
          clearable
          prefix-icon="el-icon-search"
          :placeholder="$t(`${i18nPrefix}.filter.keyword`)"
          @input="handleKeywordChange"
        />
        <div class="filter-control date-filter">
          <el-date-picker
            v-model="filters.dateRange"
            type="daterange"
            align="right"
            prefix-icon="el-icon-time"
            range-separator="-"
            value-format="yyyy-MM-dd"
            :start-placeholder="$t(`${i18nPrefix}.filter.startTime`)"
            :end-placeholder="$t(`${i18nPrefix}.filter.endTime`)"
            :default-value="defaultDateRange"
            :picker-options="pickerOptions"
            @change="handleFilterChange"
          />
        </div>
        <el-tooltip
          effect="light"
          popper-class="light-tooltip-popper"
          placement="top"
          :content="$t(`${i18nPrefix}.filter.tip`)"
        >
          <span class="filter-control filter-tip">
            <i class="el-icon-question filter-tip-icon"></i>
          </span>
        </el-tooltip>
      </template>
      <template #referrer="{ row }">
        <div class="referrer-cell">
          <span class="ranking-index">{{ row.index }}</span>
          <span v-auto-tooltip="formatEmpty(row.account_name)" class="referrer-name">
            {{ formatEmpty(row.account_name) }}
          </span>
        </div>
      </template>
    </TableList>
  </PageLayout>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import PageLayout from '@/views/adminNew/companyAdmin/pageLayout.vue'
import api from '@/api/referral'
import { addI18nProperty } from '@/i18n'
import { DATE_PICKER_SHORTCUTS } from '@/constant/common'

const EMPLOYEE_STATUS_MAP = {
  1: 'pending',
  2: 'approved',
  3: 'rejected',
  4: 'resigned',
  5: 'unregistered'
}

export default {
  name: 'ReferralLeaderboard',
  components: {
    PageLayout,
    TableList
  },
  data () {
    return {
      i18nPrefix: 'admin.referral.leaderboard',
      loading: false,
      leaderboardList: [],
      companyList: [],
      defaultDateRange: [
        moment().subtract(1, 'month').startOf('month').toDate(),
        moment().toDate()
      ],
      filters: {
        companyId: null,
        roleType: 'ee',
        dateRange: [],
        keyword: ''
      },
      sort: 'views',
      direction: 'desc',
      defaultSort: {
        prop: 'views',
        order: 'descending'
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      keywordSearchDebounce: null
    }
  },
  computed: {
    pickerOptions () {
      return {
        shortcuts: _.map(DATE_PICKER_SHORTCUTS, (item, key) => ({
          text: this.$t(`common.dateShortcut.${key}`),
          onClick (picker) {
            const start = moment().startOf(item.unit).add(item.start).toDate()
            const end = moment().startOf(item.unit).add(item.end).toDate()
            picker.$emit('pick', [start, end])
          }
        }))
      }
    },
    referrerTypeList () {
      return [
        { label: this.$t(`${this.i18nPrefix}.referrerType.all`), value: 'all' },
        { label: this.$t(`${this.i18nPrefix}.referrerType.employee`), value: 'ee' },
        { label: this.$t(`${this.i18nPrefix}.referrerType.nonEmployee`), value: 'no__ee' }
      ]
    },
    tableColumns () {
      const sortableColumn = (key, width) => ({
        label: this.$t(`${this.i18nPrefix}.column.${key}`),
        dataKey: key,
        width,
        align: 'center',
        formatNumber: true,
        sort: { enable: 'custom' }
      })
      return [
        {
          label: this.$t(`${this.i18nPrefix}.column.referrer`),
          dataKey: 'account_name',
          slotName: 'referrer',
          width: '170',
          align: 'left',
          fixed: 'left',
          showOverflowTooltip: false
        },
        {
          label: this.$t(`${this.i18nPrefix}.column.employeeStatus`),
          dataKey: 'employee_status',
          width: '150',
          align: 'left',
          formatter: this.formatEmployeeStatus
        },
        {
          label: this.$t(`${this.i18nPrefix}.column.company`),
          dataKey: 'employee_company_i18n_name',
          width: '160',
          align: 'left',
          formatter: this.formatEmpty
        },
        {
          label: this.$t(`${this.i18nPrefix}.column.employeeId`),
          dataKey: 'employee_id',
          width: '120',
          align: 'left',
          formatter: this.formatEmpty
        },
        {
          label: this.$t(`${this.i18nPrefix}.column.email`),
          dataKey: 'employee_email',
          width: '190',
          align: 'left',
          formatter: this.formatEmpty
        },
        sortableColumn('views', '90'),
        sortableColumn('submits', '160'),
        sortableColumn('applied', '130'),
        sortableColumn('interviewed', '130'),
        sortableColumn('onboard', '130'),
        sortableColumn('probation', '180'),
        sortableColumn('cash', '90'),
        sortableColumn('points', '90'),
        sortableColumn('all', '90')
      ]
    }
  },
  created () {
    this.keywordSearchDebounce = _.debounce(this.handleFilterChange, 500)
    this.initPage()
  },
  beforeDestroy () {
    this.keywordSearchDebounce.cancel()
  },
  methods: {
    ...mapActions('company', ['getHRAccessibleCompanyTree']),
    async initPage () {
      await this.loadCompanyList()
      await this.loadLeaderboard()
    },
    async loadCompanyList () {
      const companyTreeList = await this.getHRAccessibleCompanyTree()
      const currentCompanyTree = companyTreeList[0]
      const companyList = []
      _.forEach(companyTreeList, companyTree => {
        this.formatCompanyData(companyTree, companyTree.self, companyList)
      })
      this.companyList = companyList
      this.filters.companyId = currentCompanyTree.self.id
    },
    formatCompanyData (data, company, companyList) {
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')
      companyList.push(company)
      const children = data.children[company.id]
      if (children?.length) {
        _.forEach(children, child => this.formatCompanyData(data, child, companyList))
      }
      return companyList
    },
    async loadLeaderboard () {
      this.loading = true
      try {
        const resp = await api.getReferralLeaderBoard({
          company_id: this.filters.companyId,
          role_type: this.filters.roleType,
          start_date: this.filters.dateRange?.[0],
          end_date: this.filters.dateRange?.[1],
          keyword: this.filters.keyword?.trim(),
          sort: this.sort,
          direction: this.direction,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        })
        this.leaderboardList = _.map(resp.results, item => {
          addI18nProperty(item, 'employee_company_i18n_name', 'employee_company_cn_name', 'employee_company_name')
          return item
        })
        this.pagination.total = resp.total_count
      } finally {
        this.loading = false
      }
    },
    handleFilterChange () {
      this.pagination.page = 1
      this.loadLeaderboard()
    },
    handleKeywordChange () {
      this.keywordSearchDebounce()
    },
    handlePaginationChange () {
      this.loadLeaderboard()
    },
    handleSortChange (prop, orderPrefix) {
      if (!prop) {
        return
      }
      this.sort = prop
      this.direction = orderPrefix === '-' ? 'desc' : 'asc'
      this.pagination.page = 1
      this.loadLeaderboard()
    },
    formatEmployeeStatus (status) {
      const statusKey = EMPLOYEE_STATUS_MAP[status]
      return statusKey ? this.$t(`admin.company.employeeManagement.label.${statusKey}`) : '-'
    },
    formatEmpty (value) {
      return value === null || value === undefined || value === '' ? '-' : value
    }
  }
}
</script>

<style lang="scss" scoped>
.referral-leaderboard-layout {
  height: 100%;
  min-width: 1320px;
  font-size: 14px;
  line-height: 20px;
}

.referral-leaderboard-page {
  min-width: 0;

  .company-filter {
    display: flex;
    align-items: center;

    .filter-label {
      margin-right: 10px;
      white-space: nowrap;
    }

    .company-select {
      width: 160px;
    }
  }

  .referrer-type-filter {
    width: 140px;
  }

  .date-filter {
    width: 280px;

    ::v-deep {
      .el-date-editor {
        width: 100%;

        .el-range__icon,
        .el-range__close-icon {
          height: inherit;
        }

        .el-range-input {
          height: 30px;
          line-height: 30px;
        }

        .el-range-separator {
          display: inline-flex;
          flex: 1;
          height: 100%;
          align-items: center;
          justify-content: center;
        }
      }
    }
  }

  .keyword-input {
    width: 220px;
  }

  .filter-tip {
    display: inline-flex;
    align-items: center;
    height: 32px;
    color: #8c8c8c;
    cursor: help;
  }

  .filter-tip-icon {
    font-size: 16px;
  }

  ::v-deep {
    .list-head .title {
      flex: 0 0 auto;
      white-space: nowrap;
    }

    .el-table__header .cell {
      white-space: nowrap;
      word-break: keep-all;
    }
  }

  .ranking-index {
    flex: 0 0 auto;
    min-width: 28px;
    margin-right: 8px;
    color: #8c8c8c;
    text-align: left;
  }

  .referrer-cell {
    display: flex;
    width: 100%;
    min-width: 0;
    align-items: center;
  }

  .referrer-name {
    flex: 1 1 auto;
    min-width: 0;
  }
}
</style>
